// src/components/SponsorsPage.js
//
// スポンサー（提携パートナー）一覧と、会員向けクーポンの表示。
//
// 【データの取得元】
//   settings/sponsors ドキュメント1件だけを読む。
//   settings は既存ルールで「誰でも read 可」なので追加のルールは不要。
//   1回の読み取りで全件そろうため、件数が増えても課金は増えない。
//
// 【掲載期間の判定】
//   validFrom <= 今日 <= validUntil かつ active !== false のものだけ表示する。
//   端末の時計に依存するため、確実な非表示は Functions 側で active を
//   落とすことで担保する（この画面はあくまで二重のフィルタ）。
//
// 【クーポンのスクショ対策】
//   クーポンを開くと、会員名と「秒まで動く現在時刻」を重ねて表示する。
//   スクリーンショットでは時計が止まるため、店側が一目で見分けられる。
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { COLORS } from '../theme';
import { mergeTexts } from '../sponsorDefaults';

// 今日の日付を 'YYYY-MM-DD' で返す（ローカル時間基準）
const todayIso = () => {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
};

// 掲載期間内かどうか。validFrom / validUntil は空欄なら無制限として扱う。
const isOnAir = (item) => {
    if (!item) return false;
    if (item.active === false) return false;
    const t = todayIso();
    if (item.validFrom && item.validFrom > t) return false;
    if (item.validUntil && item.validUntil < t) return false;
    return true;
};

// 期限が近いかどうか（管理者向けの注意表示に使う）
const daysLeft = (validUntil) => {
    if (!validUntil) return null;
    const end = new Date(validUntil + 'T23:59:59');
    return Math.ceil((end - new Date()) / 86400000);
};

const SponsorsPage = ({ userInfo, setActiveTab, isAdmin = false }) => {
    const [config, setConfig] = useState(null);
    const [texts, setTexts] = useState(mergeTexts(null));
    const [isLoading, setIsLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('all');
    const [openCoupon, setOpenCoupon] = useState(null);   // 表示中のクーポン
    const [clock, setClock] = useState(new Date());

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'settings', 'sponsors'));
                if (!alive) return;
                const d = snap.exists() ? snap.data() : { items: [], categories: [] };
                setConfig(d);
                setTexts(mergeTexts(d.texts));
            } catch (e) {
                console.error('スポンサー情報の取得に失敗:', e);
                if (alive) setConfig({ items: [], categories: [] });
            } finally {
                if (alive) setIsLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    // クーポンを開いている間だけ時計を動かす（常時 setInterval は避ける）
    useEffect(() => {
        if (!openCoupon) return;
        const id = setInterval(() => setClock(new Date()), 1000);
        return () => clearInterval(id);
    }, [openCoupon]);

    const categories = useMemo(() => {
        const list = (config?.categories || []).slice();
        list.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
        return list;
    }, [config]);

    const visibleItems = useMemo(() => {
        const items = (config?.items || []).filter(isOnAir);
        items.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
        if (activeCategory === 'all') return items;
        return items.filter(i => i.categoryId === activeCategory);
    }, [config, activeCategory]);

    // 実際に掲載中のものが存在するカテゴリだけタブに出す（空タブを作らない）
    const usedCategoryIds = useMemo(() => {
        const s = new Set();
        (config?.items || []).filter(isOnAir).forEach(i => { if (i.categoryId) s.add(i.categoryId); });
        return s;
    }, [config]);

    if (isLoading) {
        return (
            <div className="container" style={{ paddingTop: '20px' }}>
                <div style={{ textAlign: 'center', color: '#999', padding: '60px 0' }}>読み込み中...</div>
            </div>
        );
    }

    const enabled = config?.enabled !== false;
    const memberName = userInfo?.displayName || userInfo?.name || 'メンバー';

    return (
        <div className="container" style={{ paddingTop: '15px', paddingBottom: '180px' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>🎫 提携パートナー特典</h2>
                <button
                    onClick={() => setActiveTab('home')}
                    className="btn"
                    style={{ background: 'white', border: '1px solid #ddd', fontSize: '12px', padding: '6px 12px' }}
                >
                    ← 戻る
                </button>
            </div>

            <div style={{
                background: '#f1f8e9', border: '1px solid #c5e1a5', borderRadius: '8px',
                padding: '12px', marginBottom: '18px', fontSize: '12px', color: '#33691e', lineHeight: '1.6'
            }}>
                <span style={{ whiteSpace: 'pre-wrap' }}>{texts.noticeText}</span>
                {texts.noticeCaution && (
                    <span style={{ display: 'block', marginTop: '4px', color: '#7cb342', fontSize: '11px', whiteSpace: 'pre-wrap' }}>
                        {texts.noticeCaution}
                    </span>
                )}
            </div>

            {/* カテゴリ切替（掲載が1件もないカテゴリは出さない） */}
            {categories.filter(c => usedCategoryIds.has(c.id)).length > 0 && (
                <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', marginBottom: '15px', paddingBottom: '5px' }}>
                    <button
                        onClick={() => setActiveCategory('all')}
                        style={{
                            padding: '7px 14px', borderRadius: '20px', border: 'none', whiteSpace: 'nowrap',
                            fontSize: '12px', fontWeight: 'bold', cursor: 'pointer',
                            background: activeCategory === 'all' ? COLORS.primary : '#f5f5f5',
                            color: activeCategory === 'all' ? 'white' : '#666'
                        }}
                    >
                        すべて
                    </button>
                    {categories.filter(c => usedCategoryIds.has(c.id)).map(c => (
                        <button
                            key={c.id}
                            onClick={() => setActiveCategory(c.id)}
                            style={{
                                padding: '7px 14px', borderRadius: '20px', border: 'none', whiteSpace: 'nowrap',
                                fontSize: '12px', fontWeight: 'bold', cursor: 'pointer',
                                background: activeCategory === c.id ? COLORS.primary : '#f5f5f5',
                                color: activeCategory === c.id ? 'white' : '#666'
                            }}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>
            )}

            {(!enabled || visibleItems.length === 0) ? (
                <div className="card" style={{ textAlign: 'center', color: '#999', padding: '50px 20px' }}>
                    <div style={{ fontSize: '40px', marginBottom: '10px' }}>🏪</div>
                    <p style={{ margin: 0, fontSize: '14px' }}>現在、掲載中のパートナーはありません</p>
                </div>
            ) : (
                visibleItems.map(item => {
                    const left = daysLeft(item.validUntil);
                    return (
                        <div
                            key={item.id}
                            className="card"
                            style={{ padding: '15px', marginBottom: '12px', border: '1px solid #eee' }}
                        >
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                {item.logoUrl ? (
                                    <img
                                        src={item.logoUrl}
                                        alt={item.name}
                                        style={{
                                            width: '64px', height: '64px', objectFit: 'contain',
                                            borderRadius: '8px', border: '1px solid #eee', background: 'white', flexShrink: 0
                                        }}
                                    />
                                ) : (
                                    <div style={{
                                        width: '64px', height: '64px', background: '#f5f5f5', borderRadius: '8px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '24px', flexShrink: 0
                                    }}>🏪</div>
                                )}

                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px', flexWrap: 'wrap' }}>
                                        <span style={{
                                            fontSize: '9px', background: '#eee', color: '#888',
                                            padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold'
                                        }}>PR</span>
                                        {item.categoryId && (
                                            <span style={{
                                                fontSize: '10px', background: '#e3f2fd', color: '#1565c0',
                                                padding: '2px 8px', borderRadius: '10px'
                                            }}>
                                                {categories.find(c => c.id === item.categoryId)?.label || item.categoryId}
                                            </span>
                                        )}
                                        {isAdmin && left !== null && left <= 30 && (
                                            <span style={{
                                                fontSize: '10px', background: '#ffebee', color: '#c62828',
                                                padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold'
                                            }}>
                                                残り{left}日
                                            </span>
                                        )}
                                    </div>

                                    <div style={{ fontWeight: 'bold', fontSize: '15px', color: '#333', marginBottom: '3px' }}>
                                        {item.name}
                                    </div>
                                    {item.catch && (
                                        <div style={{ fontSize: '12px', color: '#777', lineHeight: '1.5' }}>
                                            {item.catch}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                {item.couponCode && (
                                    <button
                                        onClick={() => { setClock(new Date()); setOpenCoupon(item); }}
                                        style={{
                                            flex: 2, padding: '10px', borderRadius: '8px', border: 'none',
                                            background: 'linear-gradient(135deg, #ff9800, #ffb74d)', color: 'white',
                                            fontWeight: 'bold', fontSize: '13px', cursor: 'pointer'
                                        }}
                                    >
                                        🎫 クーポンを表示
                                    </button>
                                )}
                                {item.url && (
                                    <a
                                        href={item.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            flex: 1, padding: '10px', borderRadius: '8px',
                                            border: '1px solid #ddd', background: 'white', color: '#555',
                                            fontWeight: 'bold', fontSize: '13px', textAlign: 'center',
                                            textDecoration: 'none', boxSizing: 'border-box'
                                        }}
                                    >
                                        サイト
                                    </a>
                                )}
                            </div>
                        </div>
                    );
                })
            )}

            {/* 掲載依頼への導線 */}
            <div
                onClick={() => setActiveTab('sponsorApply')}
                style={{
                    marginTop: '25px', padding: '15px', borderRadius: '10px',
                    border: '1px dashed #bbb', background: '#fafafa', cursor: 'pointer', textAlign: 'center'
                }}
            >
                <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#555', marginBottom: '3px' }}>
                    掲載をご希望の企業さまへ
                </div>
                <div style={{ fontSize: '11px', color: '#888', lineHeight: '1.6' }}>
                    知り合いのお店を紹介したい方も、こちらからお申し込みいただけます ＞
                </div>
            </div>

            {/* --- クーポン表示（スクショ対策つき） --- */}
            {openCoupon && (
                <div
                    onClick={() => setOpenCoupon(null)}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.75)', zIndex: 3000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: 'white', borderRadius: '14px', padding: '22px',
                            width: '100%', maxWidth: '340px', textAlign: 'center'
                        }}
                    >
                        <div style={{ fontSize: '12px', color: '#999', marginBottom: '6px' }}>会員クーポン</div>
                        <div style={{ fontWeight: 'bold', fontSize: '17px', color: '#333', marginBottom: '14px' }}>
                            {openCoupon.name}
                        </div>

                        <div style={{
                            border: '2px dashed #ff9800', borderRadius: '10px',
                            padding: '16px 10px', marginBottom: '14px', background: '#fff8e1'
                        }}>
                            <div style={{ fontSize: '11px', color: '#e65100', marginBottom: '6px' }}>クーポンコード</div>
                            <div style={{
                                fontSize: '24px', fontWeight: '900', letterSpacing: '2px',
                                color: '#e65100', wordBreak: 'break-all'
                            }}>
                                {openCoupon.couponCode}
                            </div>
                            {openCoupon.couponDesc && (
                                <div style={{ fontSize: '13px', color: '#5d4037', marginTop: '8px', fontWeight: 'bold' }}>
                                    {openCoupon.couponDesc}
                                </div>
                            )}
                        </div>

                        {/* ここが偽造対策。名前と秒までの時計を必ず一緒に見せる。 */}
                        <div style={{
                            background: '#263238', color: 'white', borderRadius: '8px',
                            padding: '12px', marginBottom: '12px'
                        }}>
                            <div style={{ fontSize: '11px', opacity: 0.7, marginBottom: '2px' }}>ご提示者</div>
                            <div style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '8px' }}>{memberName}</div>
                            <div style={{ fontSize: '11px', opacity: 0.7, marginBottom: '2px' }}>提示時刻（毎秒更新）</div>
                            <div style={{ fontSize: '20px', fontWeight: 'bold', fontVariantNumeric: 'tabular-nums', letterSpacing: '1px' }}>
                                {clock.toLocaleTimeString('ja-JP', { hour12: false })}
                            </div>
                            <div style={{ fontSize: '10px', opacity: 0.6, marginTop: '4px' }}>
                                {clock.toLocaleDateString('ja-JP')}
                            </div>
                        </div>

                        {openCoupon.couponNote && (
                            <div style={{ fontSize: '11px', color: '#888', textAlign: 'left', lineHeight: '1.7', marginBottom: '12px' }}>
                                {openCoupon.couponNote}
                            </div>
                        )}
                        {openCoupon.validUntil && (
                            <div style={{ fontSize: '11px', color: '#999', marginBottom: '14px' }}>
                                有効期限: {openCoupon.validUntil}
                            </div>
                        )}

                        <button
                            onClick={() => setOpenCoupon(null)}
                            className="btn"
                            style={{ width: '100%', background: '#eee', border: 'none', padding: '12px' }}
                        >
                            閉じる
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SponsorsPage;